import {
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import {
	addMoneyFromDifferentCurrencies,
	formatMoneyByIntl,
	moneyParseByDecimal,
	TMoney,
} from "@sembark-travel/money"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { Box, CheckboxInput, Spinner } from "@sembark-travel/ui-base"
import { ShareViaWhatsApp } from "@sembark-travel/ui-share"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import useSWR from "swr"
import { TTenantBrand, TTenantBrandAddress } from "../Brands/store"
import { useSynedStorageState } from "../storage"
import {
	getNotesFromHotelBookingsDetails,
	IBooking,
	IBookingDetail,
} from "./store"
import { formatTouristInfoForWhatsapp, TTourist } from "../Tourists"

type TWhatsappInclusionOptions = {
	price: boolean
	billing: boolean
	guest_list: boolean
	check_ins: boolean
	notes: boolean
	contact_details: boolean
}
type THotelBooking = {
	hotel_bookings: IBooking[]
	billing_address: TTenantBrandAddress
	tenant_brand: TTenantBrand
	greetings: string
}
export function ShareHotelBookingEnquiry({
	bookingDetails,
}: {
	bookingDetails: IBooking
}) {
	const xhr = useXHR()
	const { id: hotelBookingId } = bookingDetails
	const baseKey = `hotel_booking_whatsapp_content_${hotelBookingId}`
	const [state, setState] = useSynedStorageState<TWhatsappInclusionOptions>(
		baseKey,
		{
			price: true,
			billing: true,
			guest_list: false,
			check_ins: false,
			notes: false,
			contact_details: false,
		}
	)
	const { data: booking } = useSWR<THotelBooking>(
		[`/hotel-bookings/${hotelBookingId}/as-whatsapp`, bookingDetails],
		() =>
			xhr
				.get(`/hotel-bookings/${hotelBookingId}/as-whatsapp`, {
					params: {
						can_view_prices: 1,
					},
				})
				.then((resp) => resp.data.data)
	)

	if (!booking) {
		return <Spinner padding="4" alignCenter />
	}

	const { hotel_bookings } = booking
	const allHotelBookings = !state.check_ins
		? hotel_bookings.filter((hotel) => hotel.id === Number(hotelBookingId))
		: hotel_bookings
	const hotelDetails = allHotelBookings[0]
	const guestList = hotelDetails.tourists
	const text = getWhatsAppMessageContentForBooking({
		booking,
		state,
		hotelBookingId,
	})

	return (
		<Box position={"relative"}>
			<Box
				position="sticky"
				top="0"
				padding="4"
				zIndex="10"
				bgColor="subtle"
				borderBottomWidth="1"
				boxShadow="sm"
			>
				<Box display="flex" flexWrap="wrap" gap="4">
					<Box as="label">
						<CheckboxInput
							checked={state.price}
							onChange={() =>
								setState({
									...state,
									price: !state.price,
								})
							}
						/>{" "}
						Price
					</Box>
					<Box as="label">
						<CheckboxInput
							checked={state.billing}
							onChange={() =>
								setState({
									...state,
									billing: !state.billing,
								})
							}
						/>{" "}
						Billing
					</Box>
					{(guestList?.length ?? 0) > 1 ? (
						<Box as="label">
							<CheckboxInput
								checked={state.guest_list}
								onChange={() =>
									setState({
										...state,
										guest_list: !state.guest_list,
									})
								}
							/>{" "}
							Guest List
						</Box>
					) : null}
					{booking.hotel_bookings.length > 1 ? (
						<Box as="label">
							<CheckboxInput
								checked={state.check_ins}
								onChange={() =>
									setState({
										...state,
										check_ins: !state.check_ins,
									})
								}
							/>{" "}
							All Check-ins
						</Box>
					) : null}
					<Box as="label">
						<CheckboxInput
							checked={state.notes}
							onChange={() =>
								setState({
									...state,
									notes: !state.notes,
								})
							}
						/>{" "}
						Hotel Notes
					</Box>
					<Box as="label">
						<CheckboxInput
							checked={state.contact_details}
							onChange={() =>
								setState({
									...state,
									contact_details: !state.contact_details,
								})
							}
						/>{" "}
						Contact Details
					</Box>
				</Box>
			</Box>
			<Box>
				<ShareViaWhatsApp
					text={text}
					phoneNumber={undefined}
					trackingContentType={"hotel_bookings"}
					trackingContentId={"booking_enquiry"}
				/>
			</Box>
		</Box>
	)
}
interface GroupedBooking {
	dates: string[]
	detail: IBookingDetail
}

function getWhatsAppMessageContentForBooking({
	booking,
	hotelBookingId,
	state,
}: {
	booking: THotelBooking
	state: TWhatsappInclusionOptions
	hotelBookingId: string | number
}) {
	const { hotel_bookings, billing_address, greetings } = booking
	const allHotelBookings = !state.check_ins
		? hotel_bookings.filter((hotel) => hotel.id === Number(hotelBookingId))
		: hotel_bookings
	const hotelDetails = allHotelBookings[0]
	const guestList = hotelDetails.tourists
	const rawGreeting = greetings ?? ""
	// billing details will be in state
	const {
		price: showPricing,
		billing: showBilling,
		guest_list: showGuestList,
		notes: showNotes,
		contact_details: showContactDetails,
	} = state
	const totalPrice = addMoneyFromDifferentCurrencies(
		allHotelBookings.map(({ booked_price, currency }) =>
			moneyParseByDecimal(booked_price || 0, currency)
		)
	)
	const greeting = convertMarkdownToWhatsappFormat(rawGreeting)

	const primaryTourist = getPrimaryGuest({
		hotelGuestList: guestList,
		hotelDetails,
	})
	const guestListText = getHotelGuestListTextWithPendingDetails({
		showGuestList,
		hotelGuestList: guestList,
		hotelDetails,
	})
	const hotelDetailText = getHotelDetailText({
		hotelDetails,
		primaryTourist,
		showContactDetails,
	})
	const hotelNotes = getHotelNotes({ hotelDetails, showNotes })
	const bookingText = getBookignText({ allHotelBookings, state })
	const billingDetailsText = getBillingDetailsText({
		showBilling,
		showPricing,
		billing_address,
	})
	const totalPriceText = getTotalPriceText({ showPricing, totalPrice })

	return [
		greeting,
		hotelDetailText,
		bookingText,
		hotelNotes,
		totalPriceText,
		guestListText,
		billingDetailsText,
	]
		.filter(Boolean)
		.join("\n")
}

function getPrimaryGuest({
	hotelGuestList,
	hotelDetails,
}: {
	hotelGuestList: TTourist[] | undefined
	hotelDetails: IBooking
}): { name: string; phoneNumber: string } {
	// 1. Check for explicit primary guest in hotel guest list
	const primaryHotelGuest = hotelGuestList?.find(
		(guest) => guest.booking_tourist?.is_primary
	)
	if (primaryHotelGuest) {
		return {
			name: primaryHotelGuest.name,
			phoneNumber: primaryHotelGuest.phone_number,
		}
	}

	// 2. Check for primary tourist in trip's tourists, and see if they exist in hotel guest list
	const tripPrimaryTourist = hotelDetails.trip?.tourists?.find(
		(t) => t.is_primary
	)
	const tripPrimaryTouristInHotelList = tripPrimaryTourist
		? hotelGuestList?.find((guest) => guest.id === tripPrimaryTourist.id)
		: undefined
	if (tripPrimaryTouristInHotelList) {
		return {
			name: tripPrimaryTouristInHotelList.name,
			phoneNumber: tripPrimaryTouristInHotelList.phone_number,
		}
	}

	// 3. Fallback: first adult from hotel guest list
	const firstAdultHotelGuest = hotelGuestList?.find(
		(guest) => guest.type === "adult"
	)
	if (firstAdultHotelGuest) {
		return {
			name: firstAdultHotelGuest.name,
			phoneNumber: firstAdultHotelGuest.phone_number,
		}
	}

	// 4. Fallback: trip's primary tourist's name if available
	if (tripPrimaryTourist?.name) {
		return {
			name: tripPrimaryTourist.name,
			phoneNumber: tripPrimaryTourist.phone_number,
		}
	}

	// 5. If all else fails, return 'NA'
	return { name: "NA", phoneNumber: "NA" }
}

function getPendingDetailNotice({
	no_of_adults,
	no_of_children,
	hotelGuestList,
}: {
	no_of_adults: number
	no_of_children: number
	hotelGuestList: TTourist[] | undefined
}): string | null {
	// Count adults and children in hotelGuestList
	const adultInHotelGuestList =
		hotelGuestList?.filter((g) => g.type === "adult").length ?? 0
	const ischildInHotelGuestList =
		hotelGuestList?.filter((g) => g.type === "child").length ?? 0

	// Compute how many are missing
	const pendingAdults = Math.max(0, no_of_adults - adultInHotelGuestList)
	const pendingChildren = Math.max(0, no_of_children - ischildInHotelGuestList)
	if (pendingAdults > 0 || pendingChildren > 0) {
		const parts = []
		if (pendingAdults > 0) {
			parts.push(`${pendingAdults} more ${pluralize("adult", pendingAdults)}`)
		}
		if (pendingChildren > 0) {
			parts.push(
				`${pendingChildren} more ${pluralize("child", pendingChildren)}`
			)
		}
		return `+ ${parts.join(" and ")} (Details Pending)`
	}
	return null
}

function detailsString(
	detail: IBookingDetail,
	state: TWhatsappInclusionOptions,
	no_of_nights: number
) {
	// billing details will be in state
	const { price: showPricing } = state
	const roomDetails = [
		`  • ${detail.no_of_rooms} ${detail.room_type.display_name} (${detail.room_configuration?.per_room_details}) ${
			showPricing
				? ` X ${formatMoneyByIntl(
						moneyParseByDecimal(
							detail.per_room_booked_price || 0,
							detail.currency
						)
					)}`
				: ""
		}`,
		detail.adults_with_extra_bed > 0
			? `  • ${detail.adults_with_extra_bed} ${pluralize("Adult", detail.adults_with_extra_bed)} with Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_adult_with_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
		detail.children_with_extra_bed > 0
			? `  • ${detail.children_with_extra_bed} ${pluralize("Child", detail.children_with_extra_bed)} with Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_child_with_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
		detail.children_without_extra_bed > 0
			? `  • ${detail.children_without_extra_bed} ${pluralize("Child", detail.children_without_extra_bed)} without Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_child_without_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
	].filter(Boolean)

	return `🍽 *Meal Plan:* ${detail.meal_plan.name}
🏠 *Room Details & Rates:*
${roomDetails.join("\n")} 
   ${
			showPricing
				? `*Total:* ${formatMoneyByIntl(
						moneyParseByDecimal(detail.booked_price || 0, detail.currency)
					)} X ${no_of_nights}N = ${detail.currency} ${formatMoneyByIntl(
						moneyParseByDecimal(
							(detail.booked_price || 0) * no_of_nights,
							detail.currency
						)
					)}`
				: ""
		}`
}

function convertMarkdownToWhatsappFormat(markdownText: string): string {
	return markdownText
		.replace(/\*\*(.*?)\*\*/g, "*$1*") // Convert **bold** to *bold*
		.replace(/^#{1,6}\s*(.*)/gm, "*$1*") // Remove markdown headings (###, ##, #)
		.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1\n$2") // Convert markdown links [text](url) → text:\nurl
}

function getHotelGuestListTextWithPendingDetails({
	showGuestList,
	hotelGuestList,
	hotelDetails,
}: {
	showGuestList: boolean
	hotelGuestList: TTourist[] | undefined
	hotelDetails: IBooking
}) {
	const { no_of_adults, no_of_children } = hotelDetails
	const pendingDetailsNotice = getPendingDetailNotice({
		no_of_adults,
		no_of_children,
		hotelGuestList,
	})
	const guests = formatTouristInfoForWhatsapp(hotelGuestList, false, true)
	// const touristListText = getTouristListText(showGuestList, hotelGuestList)
	const showPendingDetails = pendingDetailsNotice
		? `\n${pendingDetailsNotice}`
		: ""
	return showGuestList
		? `${showGuestList && guests ? `\n*Guests:* ${guests}` : ``}${showPendingDetails}`
		: ""
}
function getHotelDetailText({
	hotelDetails,
	primaryTourist,
	showContactDetails,
}: {
	hotelDetails: IBooking
	primaryTourist: { name: string; phoneNumber: string }
	showContactDetails: boolean
}): string {
	return `\n*🆔 Trip ID:* ${hotelDetails.trip?.branded_id}  

🏨 *Hotel:* ${hotelDetails.hotel.name}, ${hotelDetails.hotel.location.short_name}  
${
	hotelDetails.trip
		? `👤 *Guest:* ${primaryTourist.name ? primaryTourist.name : "Name - To be updated"} ${primaryTourist && showContactDetails ? `(${primaryTourist.phoneNumber})` : ""}   
👥 *No. of Guests:* ${hotelDetails.pax_string}`
		: ""
}`
}

function getBookignText({
	allHotelBookings,
	state,
}: {
	allHotelBookings: IBooking[]
	state: TWhatsappInclusionOptions
}): string {
	const { price: showPricing } = state
	return `${allHotelBookings
		.map((booking, index) => {
			const nightsRelativeTo: Date = localOrUtcTimestampToLocalDate(
				booking.checkin_local,
				booking.checkin
			)
			const groups: Record<string, GroupedBooking> = {}

			booking.details?.forEach((item) => {
				const key = [
					item.room_type.id,
					item.meal_plan.id,
					item.adults_with_extra_bed,
					item.children_with_extra_bed,
					item.children_without_extra_bed,
					item.per_adult_with_extra_bed_booked_price ?? "",
					item.per_child_without_extra_bed_booked_price ?? "",
					item.per_child_with_extra_bed_booked_price ?? "",
					item.per_room_booked_price,
					item.booked_price,
				].join("|")

				if (!groups[key]) {
					groups[key] = {
						dates: [],
						detail: item, // keep full IBookingDetail object
					}
				}

				groups[key].dates.push(item.date)
			})

			const uniqueDetailsDateWise = Object.values(groups)

			return `${index > 0 ? `🔁 *The guest will re-check-in with the following details.*\n` : ""}
🗓️ *Check-in:* ${formatDate(localOrUtcTimestampToLocalDate(booking.overall_checkin_local, booking.overall_checkin), "DD MMM, YYYY")}  
🗓️ *Check-out:* ${formatDate(localOrUtcTimestampToLocalDate(booking.overall_checkout_local, booking.overall_checkout), "DD MMM, YYYY")}  
${booking.no_of_nights > 1 ? `🌙 *No. of Nights:* (${pluralize("Night", booking.no_of_nights, true)})\n` : ""} 
${
	uniqueDetailsDateWise.length > 1
		? uniqueDetailsDateWise
				.map(({ dates, detail }) => {
					return `${dates
						.map((date) => {
							const night = getDiff(date, nightsRelativeTo, "days") + 1
							return `🌙 *${withOrdinalSuffix(night)} Night*`
						})
						.join(",")}
${detailsString(detail, state, dates.length)}`
				})
				.join("\n")
		: booking.details?.[0]
			? `${detailsString(booking.details[0], state, booking.no_of_nights)} `
			: ""
}${
				(booking.extras?.length || 0) > 0
					? `*Other Inclusions:*  
${booking.extras
	?.map((ex) => {
		const night = ex.date
			? getDiff(
					localOrUtcTimestampToLocalDate(ex.date_local, ex.date),
					nightsRelativeTo,
					"days"
				) + 1
			: 0
		return `  • ${withOrdinalSuffix(night)} Night - ${ex.service.name} ${
			showPricing
				? `: ${formatMoneyByIntl(
						moneyParseByDecimal(ex.booked_price || 0, ex.currency),
						{ showCurrency: true }
					)}`
				: ""
		} `
	})
	.join("\n")}`
					: ""
			} `
		})
		.join("\n")}`
}

function getTotalPriceText({
	showPricing,
	totalPrice,
}: {
	showPricing: boolean
	totalPrice: TMoney[]
}) {
	return showPricing
		? `\n💰 *Total Booking Price:* ${totalPrice
				.map((p) => formatMoneyByIntl(p, { showCurrency: true }))
				.join(" + ")}`
		: ""
}

function getBillingDetailsText({
	showBilling,
	showPricing,
	billing_address,
}: {
	showBilling: boolean
	showPricing: boolean
	billing_address: TTenantBrandAddress
}): string {
	return showBilling && billing_address
		? `${showPricing ? "\n" : ""}*Payment Mode:* 
Bill to ${billing_address.addressing_name}. All Extras on Direct Basis.

🧾*Billing Instructions*

*Name:* ${billing_address.addressing_name} 
*Address:* ${billing_address.line_1}${billing_address.line_2 ? `${billing_address.line_2}` : ""}${billing_address.location.city ? `, ${billing_address.location.city.name}` : ""}${billing_address.location.state ? `, ${billing_address.location.state.name}` : ""}${billing_address.location.country ? `, ${billing_address.location.country.name}` : ""}-${billing_address.postcode}
*Details:* ${billing_address.billing_details}
`
		: ""
}

function getHotelNotes({
	hotelDetails,
	showNotes,
}: {
	hotelDetails: IBooking
	showNotes: boolean
}) {
	const { hotelNotes } = hotelDetails
		? getNotesFromHotelBookingsDetails({
				bookingDetails: hotelDetails.details,
				excludeInternals: true,
			})
		: { hotelNotes: [] }

	return `${showNotes ? `${hotelNotes.length ? `\n*🏨 Notes*\n------------------------\n *${hotelDetails.hotel.name}*\n${hotelNotes.map((hn) => `${hn.key !== "Hotel Note" ? `*${hn.key}:*\n` : ""} ${hn.descriptions.map((description) => description).join("\n")}`).join("\n")}` : ""}` : ""}`
}
